import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'phase1',
    'phase2',
    'phase3',
    'emailInput',
    'submitButton',
    'phase1Error',
    'phase2Error',
    'loading',
    'preferenceButton',
  ]

  static values = {
    currentPhase: { type: Number, default: 1 },
    email: { type: String, default: '' },
    preference: { type: String, default: '' },
    apiUrl: { type: String, default: '/api/newsletter_signups' },
    isSubmitting: { type: Boolean, default: false },
  }

  connect() {
    // Ensure Phase 1 is visible on connect
    this.showPhase(1)
  }

  // ===== Phase 1: Email Input =====

  handleInput() {
    // Clear errors when user starts typing
    if (this.hasPhase1ErrorTarget) {
      this.phase1ErrorTarget.classList.add('d-none')
      this.phase1ErrorTarget.textContent = ''
    }
  }

  handleEmailSubmit(event) {
    event.preventDefault()

    // Prevent double submission
    if (this.isSubmittingValue) {
      return
    }

    const email = this.emailInputTarget.value.trim().toLowerCase()

    // Validate email
    if (!this.validateEmail(email)) {
      this.showError('Please enter a valid email address.', 1)
      return
    }

    // Sanitize and store email
    this.emailValue = email
    this.emailInputTarget.value = email

    // Set submitting flag
    this.isSubmittingValue = true
    this.submitButtonTarget.disabled = true

    // Transition to Phase 2
    this.transitionPhase(1, 2)

    // Re-enable after transition completes
    setTimeout(() => {
      this.isSubmittingValue = false
      this.submitButtonTarget.disabled = false
    }, 700)
  }

  validateEmail(email) {
    if (!email) return false

    // HTML5 validity check
    if (this.emailInputTarget.validity && !this.emailInputTarget.validity.valid) {
      return false
    }

    // Additional regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // ===== Phase 2: Preference Selection =====

  handlePreferenceSelect(event) {
    const button = event.currentTarget
    const preference = button.dataset.preference

    if (!preference) {
      console.error('No preference data attribute found on button')
      return
    }

    this.preferenceValue = preference
    this.submitToAPI()
  }

  handleKeyPress(event) {
    // Handle Space and Enter keys for preference buttons
    // Note: This is needed for type="button" elements which don't have native
    // Space/Enter handling. Submit buttons handle this natively via form submission.
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      this.handlePreferenceSelect(event)
    }
  }

  async submitToAPI() {
    // Disable all preference buttons
    this.disablePreferenceButtons()

    // Show loading spinner and set ARIA busy
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
    if (this.hasPhase2Target) {
      this.phase2Target.setAttribute('aria-busy', 'true')
    }

    // Create AbortController for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    try {
      const response = await fetch(this.apiUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email_address: this.emailValue,
          mailing_list: 'root_functional_medicine_list_id',
          interest: [this.preferenceValue],
        }),
        signal: controller.signal,
      })

      // Clear timeout on successful response
      clearTimeout(timeoutId)

      const data = await response.json()

      // Hide loading spinner and remove ARIA busy
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('d-none')
      }
      if (this.hasPhase2Target) {
        this.phase2Target.setAttribute('aria-busy', 'false')
      }

      if (response.ok && data.success !== false) {
        // Success - transition to Phase 3
        this.transitionPhase(2, 3)
      } else {
        // API returned error
        const errorMessage = data.last_error || 'An error occurred. Please try again.'
        this.showError(errorMessage, 2)
        this.enablePreferenceButtons()
      }
    } catch (error) {
      // Clear timeout
      clearTimeout(timeoutId)

      // Hide loading spinner and remove ARIA busy
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('d-none')
      }
      if (this.hasPhase2Target) {
        this.phase2Target.setAttribute('aria-busy', 'false')
      }

      // Handle timeout separately
      if (error.name === 'AbortError') {
        console.error('Newsletter signup timeout')
        this.showError('Request timed out. Please try again.', 2)
      } else {
        // Network or fetch error
        console.error('Newsletter signup error:', error)
        this.showError('Network error. Please check your connection and try again.', 2)
      }
      this.enablePreferenceButtons()
    }
  }

  disablePreferenceButtons() {
    if (this.hasPreferenceButtonTarget) {
      this.preferenceButtonTargets.forEach((button) => {
        button.disabled = true
        button.style.cursor = 'not-allowed'
        button.style.opacity = '0.6'
      })
    }
  }

  enablePreferenceButtons() {
    if (this.hasPreferenceButtonTarget) {
      this.preferenceButtonTargets.forEach((button) => {
        button.disabled = false
        button.style.cursor = 'pointer'
        button.style.opacity = '1'
      })
    }
  }

  // ===== Phase Transitions =====

  transitionPhase(fromPhase, toPhase) {
    const fromTarget = this[`phase${fromPhase}Target`]
    const toTarget = this[`phase${toPhase}Target`]

    // Fade out current phase
    fromTarget.classList.add('newsletter-fade-out')

    // Wait for fade out animation
    setTimeout(() => {
      // Hide old phase
      fromTarget.classList.add('d-none')
      fromTarget.classList.remove('newsletter-fade-out')

      // Show new phase
      toTarget.classList.remove('d-none')
      toTarget.classList.add('newsletter-fade-in')

      // Update current phase value
      this.currentPhaseValue = toPhase

      // Announce phase change for screen readers
      this.announcePhaseChange(toPhase)

      // Remove fade-in class after animation completes
      setTimeout(() => {
        toTarget.classList.remove('newsletter-fade-in')

        // Move focus to first interactive element
        this.focusFirstInteractive(toTarget)
      }, 400)
    }, 300)
  }

  // eslint-disable-next-line class-methods-use-this
  focusFirstInteractive(container) {
    // Add timing buffer to allow for state transitions (e.g., disabled buttons becoming enabled)
    setTimeout(() => {
      // Find ALL buttons first (including temporarily disabled ones)
      const allButtons = container.querySelectorAll('button')

      // Try to focus first enabled button
      for (const button of allButtons) {
        if (!button.disabled) {
          button.focus()
          return
        }
      }

      // Fallback: Try other focusable elements (input, links)
      const otherFocusable = container.querySelector('input:not([disabled]), a[href]')
      if (otherFocusable) {
        otherFocusable.focus()
        return
      }

      // Phase 3 fallback: Focus on confirmation icon or container
      const confirmationIcon = container.querySelector('.confirmation-icon i, .bi-check-circle')
      if (confirmationIcon) {
        // Make icon focusable if in Phase 3
        confirmationIcon.setAttribute('tabindex', '-1')
        confirmationIcon.focus()
        return
      }

      // Final fallback: Focus container itself
      if (container) {
        container.setAttribute('tabindex', '-1')
        container.focus()
      }
    }, 100) // 100ms buffer for state transitions
  }

  // eslint-disable-next-line class-methods-use-this
  announcePhaseChange(phase) {
    // Create or update screen reader announcement
    let announcer = document.getElementById('newsletter-sr-announcer')

    if (!announcer) {
      announcer = document.createElement('div')
      announcer.id = 'newsletter-sr-announcer'
      announcer.className = 'visually-hidden'
      announcer.setAttribute('aria-live', 'polite')
      announcer.setAttribute('aria-atomic', 'true')
      document.body.appendChild(announcer)
    }

    const announcements = {
      1: 'Newsletter signup form, enter your email address',
      2: 'Select your preference: for business or for yourself',
      3: 'Thank you for subscribing to our newsletter',
    }

    announcer.textContent = announcements[phase] || ''
  }

  showPhase(phaseNumber) {
    // Hide all phases
    if (this.hasPhase1Target) this.phase1Target.classList.add('d-none')
    if (this.hasPhase2Target) this.phase2Target.classList.add('d-none')
    if (this.hasPhase3Target) this.phase3Target.classList.add('d-none')

    // Show requested phase
    const phaseTarget = this[`phase${phaseNumber}Target`]
    if (phaseTarget) {
      phaseTarget.classList.remove('d-none')
    }
  }

  // ===== Error Handling =====

  showError(message, phase = 1) {
    const errorTarget = phase === 1 ? 'phase1Error' : 'phase2Error'
    const targetName = `${errorTarget}Target`

    if (this[`has${errorTarget.charAt(0).toUpperCase() + errorTarget.slice(1)}Target`]) {
      this[targetName].textContent = message
      this[targetName].classList.remove('d-none')
      this[targetName].setAttribute('role', 'alert')
      this[targetName].setAttribute('aria-live', 'polite')
    }
  }

  // ===== Utilities =====

  get csrfToken() {
    try {
      const meta = document.querySelector('meta[name="csrf-token"]')
      if (!meta || !meta.content) {
        throw new Error('CSRF token not found')
      }
      return meta.content
    } catch (error) {
      console.error('CSRF token error:', error)
      this.showError('Security token not found. Please refresh the page.', this.currentPhaseValue)
      throw error
    }
  }
}
